import { stockApi } from './stockApi';
import { sha256, stableJSON, sourceParts, parsedParts } from './stockHistory';

// A report and its bytes are written locally before the network save begins.
// Removing a queued item only happens after readback from stock_uploads.
const pending = new Map();
const listeners = new Set();
const preparations = new Map();
let hydrated = false, running = false, rerun = false, database, lastSaved = null;
let snapshot = { pending:[], lastSaved:null, running:false };
function publish() {
  snapshot={pending:[...pending.values()].map(t=>({id:t.id,team:t.team,kind:t.kind,status:t.status,error:t.error,durable:t.durable})),lastSaved,running};
  for (const listener of listeners) listener(snapshot);
}
export function subscribeStock(listener) { listeners.add(listener); listener(snapshot); return ()=>listeners.delete(listener); }
function db() {
  if (!database) database=new Promise((resolve,reject)=>{
    const request=indexedDB.open('razergrid-stock-queue',1);
    request.onupgradeneeded=()=>request.result.createObjectStore('pending',{keyPath:'id'});
    request.onsuccess=()=>resolve(request.result); request.onerror=()=>reject(request.error);
  });
  return database;
}
async function stored(action,value) {
  const database=await db();
  return new Promise((resolve,reject)=>{
    const tx=database.transaction('pending',action==='getAll'?'readonly':'readwrite');
    const request=tx.objectStore('pending')[action](...(value===undefined?[]:[value]));
    tx.oncomplete=()=>resolve(request.result); tx.onerror=()=>reject(tx.error); tx.onabort=()=>reject(tx.error || new Error('Local save was interrupted'));
  });
}
export async function startStockQueue() {
  if (!hydrated) {
    hydrated=true;
    try { for(const task of await stored('getAll')) if(!pending.has(task.id)) pending.set(task.id,{...task,durable:true}); }
    catch { /* Individual captures show local-storage failures explicitly. */ }
    publish();
  }
  return retryStockSaves();
}
export async function retryStockSaves() {
  if(running) { rerun=true; return; }
  running=true; publish();
  try {
    // Sources always precede derived runs, including after a browser restart.
    const tasks=[...pending.values()].filter(t=>t.event).sort((a,b)=>(a.kind==='parsed')-(b.kind==='parsed') || a.event.observed_at.localeCompare(b.event.observed_at));
    for(const task of tasks) {
      if((task.dependsOn && pending.has(task.dependsOn)) || (task.kind==='parsed' && Object.values(task.event.sources || {}).some(id=>pending.has(id)))) continue;
      task.status='Saving to server'; task.error=''; publish();
      try {
        const saved=await stockApi.save(task);
        await stored('delete',task.id).catch(()=>{});
        pending.delete(task.id);
        lastSaved={...saved,team:task.team,kind:task.kind,at:new Date().toISOString()};
      } catch(error) {
        task.status='Save pending'; task.error=error.message;
        const {prepare,...persisted}=task;
        await stored('put',persisted).catch(()=>{});
      }
      publish();
    }
  } finally { running=false; publish(); if(rerun) { rerun=false; retryStockSaves(); } }
}
function enqueue(id,team,kind,prepare) {
  const task={id,team,kind,status:'Preparing archive',durable:false};
  pending.set(id,task); publish();
  task.prepare=prepare;
  preparations.set(id,prepareTask(task));
  return {eventId:id,team,kind};
}
async function prepareTask(task) {
  try {
    const prepared=await task.prepare();
    Object.assign(task,prepared,{status:'Save pending'});
    // Functions cannot be structured-cloned into IndexedDB.
    const {prepare,...persisted}=task;
    await stored('put',persisted); task.durable=true;
  } catch(error) { task.error='Local archive: '+error.message; task.status='Save pending'; }
  publish();
  if(task.event) {
    // A capture can finish preparing while another save is in flight. Schedule
    // another pass when that pass settles, rather than stranding the new item.
    retryStockSaves();
  }
}
export async function retryStockArchive() {
  for(const task of pending.values()) if(!task.event && task.prepare) await prepareTask(task);
  return retryStockSaves();
}
export function enqueueStockSource({kind,file,rows,team,observedAt,configuration={},raw={}}) {
  const id=crypto.randomUUID();
  const at=observedAt || new Date().toISOString();
  return enqueue(id,team,kind,async()=>{
    if(!team) throw new Error('Select a team before archiving this report.');
    const hash=await sha256(await file.arrayBuffer());
    const scope=await sha256(kind==='powerbi'?'powerbi-v1':stableJSON(configuration));
    return {file,event:{id,team,kind,file_hash:hash,scope_hash:scope,observed_at:at,filename:file.name,original_path:hash},parts:sourceParts(kind,rows),document:JSON.parse(stableJSON({version:1,rows,raw,configuration,file:{name:file.name,size:file.size,type:file.type,lastModified:file.lastModified},reportedAt:at}))};
  });
}
export function enqueueStockParsed({report,team,sources,configuration,observedAt,metadata={}}) {
  const id=crypto.randomUUID();
  return enqueue(id,team,'parsed',async()=>({
    event:{id,team,kind:'parsed',file_hash:await sha256(id),scope_hash:await sha256(stableJSON(configuration)),observed_at:observedAt || new Date().toISOString(),filename:'parsed-part-report.json',sources},
    parts:parsedParts(report),document:JSON.parse(stableJSON({version:1,report,configuration,...metadata})),
  }));
}

export function enqueueStockCallsRevision({source,rows,team,configuration,raw}) {
  const id=crypto.randomUUID();
  return enqueue(id,team,'calls',async()=>{
    if(!source || source.team!==team) throw new Error('Upload the calls original for this team before archiving a rebuilt list.');
    await preparations.get(source.eventId);
    const queued=pending.get(source.eventId);
    const previous=queued?.event || await stockApi.source(source.eventId);
    return {dependsOn:source.eventId,event:{id,team,kind:'calls',file_hash:previous.file_hash,original_path:previous.original_path,scope_hash:await sha256(stableJSON(configuration)),observed_at:previous.observed_at,filename:previous.filename},parts:sourceParts('calls',rows),document:JSON.parse(stableJSON({version:1,rows,raw,configuration,rebuiltFrom:source.eventId}))};
  });
}
